import React from 'react';
import { Box, Container, Typography, Toolbar, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import WifiIcon from '@mui/icons-material/Wifi';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import HotelIcon from '@mui/icons-material/Hotel';
import BusinessIcon from '@mui/icons-material/Business';
import WaterIcon from '@mui/icons-material/Water';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import DomainIcon from '@mui/icons-material/Domain';
import SpaIcon from '@mui/icons-material/Spa';
import PoolIcon from '@mui/icons-material/Pool';
import FooterSection from '../components/FooterSection';

const specs = [
  { label: 'Radio Protocol', value: 'LoRa EU 868 MHz' },
  { label: 'Data Frequency', value: 'Up to 1 min' },
  { label: 'Battery Type', value: 'Lithium 2600mAh (non-rechargeable)' },
  { label: 'Battery Lifetime', value: '3+ years (at 1-min reporting interval)' },
  { label: 'Probe Points per Sensor', value: 'Up to 3' },
  { label: 'Working Temperature', value: '0°C to +85°C' },
  { label: 'Temperature Accuracy', value: '±2°C' },
  { label: 'Measurement Technology', value: 'Non-invasive (surface measurement)' },
  { label: 'Antenna Range', value: 'Up to 8km (line of sight in open air)' },
  { label: 'Certification', value: 'CE, UKCA certified' },
  { label: 'Safety Compliance', value: 'RoHS 3 compliant' },
  { label: 'Additional Protection', value: 'Reverse battery installation & short circuit protection' },
  { label: 'Sensor Weight', value: '69g (without battery)' },
  { label: 'Probe Weight', value: '40g (2 Probe), 60g (3 Probe)' },
  { label: 'Sensor Dimensions', value: '110mm × 57mm × 29mm' },
  { label: 'Probe Dimensions (body)', value: '6mm × 6mm × 20mm' },
  { label: 'Probe Lead Length', value: '1.5m' },
  { label: 'Sensor IP Rating', value: 'Not rated (Indoor use only)' },
  { label: 'Probe IP Rating', value: 'IP67' },
];

const industries = [
  {
    icon: <BusinessIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Facilities Management',
    description:
      'Monitor critical assets remotely, generate actionable reports, and respond to issues with speed and precision.',
  },
  {
    icon: <WaterIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Water Treatment',
    description:
      'A scalable, flexible solution tailored to your operational needs - start with a few sensors and expand as your network grows.',
  },
  {
    icon: <HotelIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Hotels & Restaurants',
    description:
      'Seamlessly integrated with no disruption to daily operations - delivering powerful insights that enhance safety, compliance, and guest experience.',
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Schools & Hospitals',
    description:
      'Protect occupants with continuous temperature monitoring across all water systems, supporting duty of care and regulatory compliance.',
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Public Listed Buildings',
    description:
      'Retrofitted solution with a quick install process even in hard-to-reach places, leaving no footprint in precious spaces.',
  },
  {
    icon: <DomainIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Government Buildings',
    description:
      'Centralised monitoring across multiple sites, supporting compliance obligations and providing auditable records for public sector accountability.',
  },
  {
    icon: <SpaIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'ESG Compliance',
    description:
      'Support environmental and governance goals with verified data on water safety management, contributing to ESG reporting and sustainability frameworks.',
  },
  {
    icon: <PoolIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Leisure & Fitness Centres',
    description:
      'Manage Legionella risk across complex water systems in gyms, swimming pools, and spa facilities - meeting ACOP L8 obligations with minimal manual intervention.',
  },
];

const DarwinPage = () => {
  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#000000',
          py: { xs: 8, md: 12 },
          borderBottom: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src="./darwin_hero.jpg"
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            filter: 'grayscale(100%) brightness(0.28)',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,77,64,0.18) 0%, rgba(0,0,0,0.55) 100%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Specification"
            size="small"
            sx={{
              fontFamily: 'Outfit',
              fontSize: '0.7rem',
              mb: 2,
              backgroundColor: '#004D40',
              color: '#ffffff',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Darwin Sensor
          </Typography>
          <Typography
            component="p"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#004D40',
              mb: 1.5,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Pipe Temperature Monitoring
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 580,
            }}
          >
            Our innovative approach to remote temperature sensing and data utilisation makes ACOP L8
            and HSG274 compliance a breeze. Set multiple rules on each pipe to preempt faults before
            they occur.
          </Typography>

          {/* Feature badges */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
            {[
              { icon: <ThermostatIcon sx={{ fontSize: 16 }} />, label: 'High Accuracy' },
              { icon: <WifiIcon sx={{ fontSize: 16 }} />, label: 'Wireless' },
              { icon: <BatteryFullIcon sx={{ fontSize: 16 }} />, label: 'Long-Life Battery' },
              { icon: <RouterOutlinedIcon sx={{ fontSize: 16 }} />, label: 'LoRaWAN' },
            ].map((b) => (
              <Box
                key={b.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: '1px solid rgba(0,77,64,0.7)',
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.75,
                  color: 'rgba(255,255,255,0.8)',
                  fontFamily: 'Outfit',
                  fontSize: '0.82rem',
                }}
              >
                {b.icon}
                {b.label}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Spec table */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  color: '#000000',
                  mb: 4,
                  letterSpacing: '-0.02em',
                }}
              >
                Technical Specifications
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {specs.map((spec, i) => (
                  <Box
                    key={spec.label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 3,
                      py: 1.75,
                      backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa',
                      borderBottom: i < specs.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        color: '#444444',
                      }}
                    >
                      {spec.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: '#000000',
                      }}
                    >
                      {spec.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              {/* Product visual */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '3/4',
                  borderRadius: '10px',
                  border: '1px solid #ffffff',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src="./EdenBlack2.png"
                  alt="Darwin Sensor"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Who is it for */}
      <Box sx={{ backgroundColor: '#fafafa', py: { xs: 8, md: 12 }, borderTop: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 500,
                fontSize: '0.78rem',
                color: '#004D40',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Who is Darwin for?
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 700,
                fontSize: { xs: '1.9rem', md: '2.5rem' },
                color: '#000000',
                letterSpacing: '-0.02em',
              }}
            >
              Built for industries where compliance is critical
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {industries.map((item) => (
              <Grid key={item.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box
                  sx={{
                    p: { xs: 3.5, md: 4 },
                    backgroundColor: '#ffffff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'border-color 0.25s, box-shadow 0.25s',
                    '&:hover': {
                      borderColor: '#004D40',
                      boxShadow: '0 4px 24px rgba(0,77,64,0.08)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2.5 }}>{item.icon}</Box>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#000000',
                      mb: 1.25,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 400,
                      fontSize: '0.9rem',
                      color: '#666666',
                      lineHeight: 1.75,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default DarwinPage;
